import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Icon,
  Input,
  InputGroup,
} from '@appfolio/react-gears';

import classnames from 'classnames';
import { isEqual as equal } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { findDOMNode } from 'react-dom';
import type { DropdownProps, InputProps } from 'reactstrap';
import DropdownOptions from './DropdownOptions';

export type Direction = 'up' | 'down';

export type Option<T> = {
  label: string;
  value: T;
  disabled?: boolean;
};

export type OptionGroup<T> = {
  label: string;
  options: Option<T>[];
};

export interface ComboboxProps<T> {
  className?: InputProps['className'];
  clearable?: boolean;
  disabled?: InputProps['disabled'];
  options: (Option<T> | OptionGroup<T>)[];
  direction?: Direction;
  dropdownProps?: DropdownProps;
  filterOptions?: (options: Option<T>[], value: string) => Option<T>[];
  id?: InputProps['id'];
  isValidNewOption?: (label: string) => boolean;
  menuMaxHeight?: string;
  name?: InputProps['name'];
  noResultsLabel?: string;
  placeholder?: InputProps['placeholder'];
  onChange?: (value: T | undefined) => void;
  onChangeWithLabel?: (value: T | undefined, label: string | undefined) => void;
  onChangeText?: (value: string) => void;
  onCreate?: (str: string) => T;
  type?: InputProps['type'];
  value?: T;
}

const defaultProps = {
  clearable: true,
  menuMaxHeight: '12rem',
  noResultsLabel: 'No results found',
  onChange: () => {},
  onChangeWithLabel: () => {},
  onChangeText: () => {},
  filterOptions: (o: Option<any>[], v: any) =>
    o.filter(option =>
      v ? option.label.toLowerCase().indexOf(v.toLowerCase()) > -1 : true
    ),
  isValidNewOption: () => true,
  placeholder: 'Select...',
};

function isKeyboardEvent<T>(u: unknown): u is React.KeyboardEvent<T> {
  return !!(u && (u as any).key);
}

export function makeKey<T>(value: T) {
  switch (typeof value) {
    case 'string':
      return encodeURIComponent(value).replace('/%/g', '-');
    case 'number':
      return String(value);
    default:
      return JSON.stringify(value).replace(/[^a-z0-9.:_-]+/gi, '-');
  }
}

/**
 * Type guard for Option.
 */
export function isOption(u: unknown): u is Option<any> {
  return typeof u === 'object' && !!u && 'value' in u && 'label' in u;
}

/**
 * Type guard for OptionGroup.
 */
export function isOptionGroup<T>(
  option: Option<T> | OptionGroup<T>
): option is OptionGroup<T> {
  return 'options' in option && Array.isArray(option.options);
}

/**
 * Combobox component forked from react-gears 7. Changelog vs. upstream:
 *   - styling to mimic Select (react-select-plus)
 *   - clearable prop, for parity with Select
 *   - a11y and keyboard navigation enhancements
 *   - onChangeWithLabel event (simultaneous with onChange) to facilitate use with forms and multi-select
 *   - onChangeText event to hook into typing to facilitate dynamic options
 *
 * @see https://github.com/appfolio/react-gears/blob/master/src/components/Combobox/Combobox.tsx
 */
function Combobox<T>({
  className,
  clearable = defaultProps.clearable,
  direction,
  disabled,
  dropdownProps,
  id,
  options: optionsProp,
  placeholder,
  value,
  menuMaxHeight = defaultProps.menuMaxHeight,
  noResultsLabel = defaultProps.noResultsLabel,
  onChange = defaultProps.onChange,
  onChangeWithLabel = defaultProps.onChangeWithLabel,
  onChangeText = defaultProps.onChangeText,
  onCreate,
  isValidNewOption = defaultProps.isValidNewOption,
  filterOptions = defaultProps.filterOptions,
  ...props
}: ComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [visibleOptions, setVisibleOptions] = useState<Option<T>[]>([]);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number>(0);

  const inputElement = useRef<HTMLInputElement>(null);
  const dropdownMenu = useRef(null);
  const focusedOption = useRef(null);

  const options: Option<T>[] = useMemo(() => {
    if (equal(optionsProp, []) || !optionsProp) {
      return [];
    }

    const flattenOptions = (
      opts: (Option<T> | OptionGroup<T>)[]
    ): Option<T>[] =>
      opts.flatMap(opt =>
        isOptionGroup(opt) ? flattenOptions(opt.options) : [opt]
      );

    return flattenOptions(optionsProp);
  }, [optionsProp]);

  const selected = useMemo<Option<T> | undefined>(
    () => options.find(option => equal(option.value, value)),
    [value, options]
  );
  const selectedLabel = selected?.label;
  const noMatches = visibleOptions.length === 0;
  const cursorAtStart = () =>
    inputElement?.current?.selectionStart === 0 &&
    inputElement?.current?.selectionEnd === 0;

  // TODO: why did react-gears include this behavior? it's annoying!
  //   - user opens the dropdown with keyboard nav (UpArrow/DownArrow)
  //   - we reset the focused option to 0!
  // By commenting it out, we have superior UX...
  // useEffect(() => {
  //   if (visibleOptions.length > 0) {
  //     setFocusedOptionIndex(0);
  //   }
  // }, [visibleOptions]);

  // When typing reopens the menu (see the Input onChange handler), the
  // open-flip must not re-sync the input text below — that would wipe the
  // character the user just typed.
  const openedByTypingRef = useRef(false);

  useEffect(() => {
    // Consumed by the caret effect below, which runs after this one.
    if (openedByTypingRef.current) {
      return;
    }
    if (selectedLabel) {
      setInputValue(selectedLabel);
    } else if (value === undefined) {
      setInputValue('');
    }
  }, [open, selectedLabel, value]);

  useEffect(() => {
    // A typing-triggered open must not touch the user's selection — a
    // select-all mid-burst would make the next character wipe what was
    // just typed.
    if (openedByTypingRef.current) {
      openedByTypingRef.current = false;
      return;
    }
    // Opening with a committed label highlights it (matching native
    // tab-focus behavior) so typing starts a fresh query.
    if (open && selectedLabel && inputElement?.current) {
      window.setTimeout(() => {
        inputElement?.current?.select();
      }, 1);
    }
  }, [open, selectedLabel]);

  useEffect(() => {
    const selectableOptions = [...options];

    setVisibleOptions(
      filterOptions(
        selectableOptions,
        selected && inputValue === (selected as Option<T>).label
          ? ''
          : inputValue
      )
    );
  }, [inputValue, setVisibleOptions, filterOptions, options, value, selected]);

  const scrollFocusedOptionIntoView = () => {
    if (dropdownMenu.current === null || focusedOption.current === null) {
      return;
    }
    /* eslint-disable react/no-find-dom-node */
    const focusedOptionNode = findDOMNode(focusedOption.current) as HTMLElement;
    const menuNode = findDOMNode(dropdownMenu.current) as HTMLElement;
    /* eslint-enable react/no-find-dom-node */

    if (focusedOptionNode === null || menuNode === null) {
      return;
    }

    const scrollTop = menuNode.scrollTop;
    const scrollBottom = scrollTop + menuNode.offsetHeight;
    const optionTop = focusedOptionNode.offsetTop;
    const optionBottom = optionTop + focusedOptionNode.offsetHeight;

    if (scrollTop > optionTop) {
      menuNode.scrollTop -= scrollTop - optionTop;
    } else if (scrollBottom < optionBottom) {
      menuNode.scrollTop += optionBottom - scrollBottom;
    }
  };

  useEffect(scrollFocusedOptionIntoView, [focusedOptionIndex]);

  const selectOption = (value: T, label: string) => {
    onChange(value);
    onChangeWithLabel(value, label);
    setOpen(false);
  };

  const clear = () => {
    setInputValue('');
    onChange(undefined);
    onChangeWithLabel(undefined, undefined);
    setOpen(false);
  };

  const showSelectedPreview = () => {
    setInputValue(`${(selected as Option<T>).label} `);
    window.setTimeout(() => {
      inputElement?.current?.setSelectionRange(0, 0);
    }, 1);
  };

  const clearSelectedPreview = (e?: React.SyntheticEvent<HTMLInputElement>) => {
    if (selected && (selected as Option<T>).label === inputValue) {
      if (cursorAtStart()) {
        if (isKeyboardEvent(e)) {
          setInputValue(e.key);
        } else {
          setInputValue('');
        }
      }
    }
  };

  const createOption = () => {
    if (!onCreate) {
      return;
    }

    const optionValue = onCreate(inputValue);
    if (optionValue) {
      selectOption(optionValue, inputValue);
    }
  };

  const handleOptionsKeyboardNav = (e: React.KeyboardEvent<HTMLElement>) => {
    const input = inputElement?.current;
    const allSelected =
      input?.selectionStart === 0 && input.selectionEnd === input.value.length;
    const isDisplayingSelected =
      selected && inputValue === (selected as Option<T>).label;

    switch (e.key) {
      case 'ArrowDown':
        if (!open) {
          setOpen(true);
        } else if (focusedOptionIndex < visibleOptions.length - 1) {
          setFocusedOptionIndex(focusedOptionIndex + 1);
        }
        break;
      case 'ArrowUp':
        if (!open) {
          setOpen(true);
        } else if (focusedOptionIndex > 0) {
          setFocusedOptionIndex(focusedOptionIndex - 1);
        }
        break;
      case 'Backspace':
        if (selected) {
          if (
            clearable &&
            (cursorAtStart() || (allSelected && isDisplayingSelected))
          ) {
            clear();
          } else if (
            (input?.selectionStart === 1 && input?.selectionEnd === 1) ||
            (allSelected && !isDisplayingSelected)
          ) {
            showSelectedPreview();
          }
        }
        break;
      case 'Enter':
        // When the dropdown is closed there is no focused option to commit, so
        // let Enter bubble untouched (e.g. to a parent "press Enter to
        // apply/submit" handler). Only intercept Enter while the menu is open.
        if (!open) {
          return;
        }
        if (noMatches) {
          createOption();
          return;
        }
        selectOption(
          visibleOptions[focusedOptionIndex].value,
          visibleOptions[focusedOptionIndex].label
        );
        e.preventDefault();
        break;
      case 'Escape':
        if (open) {
          setOpen(false);
        } else if (clearable) {
          clear();
        }
        break;
      case 'Tab': {
        // Commit the focused option, but only when the user has typed a
        // pending query — the menu opens on mere focus, so committing on
        // every Tab would select an option just by tabbing through the
        // control. No preventDefault: focus must advance to the next
        // control (state flushes before the browser moves focus, so a
        // control this commit enables becomes tabbable in time).
        if (!open || noMatches || !inputValue || isDisplayingSelected) {
          return;
        }
        const option = visibleOptions[focusedOptionIndex] ?? visibleOptions[0];
        selectOption(option.value, option.label);
        break;
      }
      default:
        return;
    }
  };

  const renderNoOptions = () => {
    if (onCreate) {
      return (
        <DropdownItem
          active={noMatches}
          data-testid="create-new-option"
          disabled={!isValidNewOption(inputValue)}
          onMouseDown={ev => {
            ev.preventDefault();
            ev.stopPropagation();
            createOption();
          }}
        >
          {`Create "${inputValue}"`}
        </DropdownItem>
      );
    }

    return <DropdownItem disabled>{noResultsLabel}</DropdownItem>;
  };

  // Suppress "X" button on WebKit browsers; we do our own!
  const typeProp = props.type || 'search';
  const inputType = typeProp === 'search' ? 'text' : typeProp;
  clearable = clearable && !!value && !disabled && typeProp === 'search';

  return (
    <Dropdown
      className={classnames('combobox', className)}
      data-testid="combobox-dropdown"
      direction={direction}
      isOpen={!disabled && open}
      toggle={() => {}}
      onBlur={({ currentTarget: blurred, relatedTarget: focused }) => {
        if (focused instanceof Node && blurred.contains(focused)) {
          return;
        }
        setOpen(false);
      }}
    >
      <DropdownToggle disabled={disabled} tag="div">
        <InputGroup className={className}>
          <Input
            aria-label="Combobox"
            autoComplete="off"
            data-testid="combobox-input"
            disabled={disabled}
            id={id}
            innerRef={inputElement}
            onClick={() => {
              if (!open) {
                setOpen(true);
              }
            }}
            onFocus={e => {
              e.preventDefault();
              e.stopPropagation();
              setOpen(true);
            }}
            onChange={e => {
              e.preventDefault();
              e.stopPropagation();
              const { value } = e.target;
              setInputValue(value);
              onChangeText(value);

              // Typing must surface the options. The input can be focused
              // while the menu is closed (mount-time autoFocus fires before
              // React's onFocus subscription; Escape also closes without
              // blurring), and a hidden menu makes typing look inert.
              if (!open) {
                openedByTypingRef.current = true;
                setOpen(true);
              }

              if (selected && cursorAtStart() && value === '') {
                // TODO: document what's going on here
                //   - should this be a call to clear()
                //   - or is it important to keep focus on the input?
                //   - does this event make sense for non-clearable comboboxen?
                onChange(undefined);
                onChangeWithLabel(undefined, undefined);
              }
            }}
            onMouseDown={e => {
              // Clicking a combobox that displays its committed label
              // selects the whole label — the standard "type to replace"
              // affordance — instead of parking the caret at position 0,
              // which reads as a cursor glitch. preventDefault keeps the
              // browser's default mousedown from collapsing the selection
              // back to a caret. Mid-edit (label differs from the text),
              // the click places the caret normally.
              if (selected && (selected as Option<T>).label === inputValue) {
                inputElement?.current?.select();
                e.preventDefault();
              }

              // @ts-expect-error 2339 it lies!
              e.target.focus();
            }}
            onKeyDown={handleOptionsKeyboardNav}
            onKeyPress={clearSelectedPreview}
            onPaste={clearSelectedPreview}
            placeholder={selected ? undefined : placeholder}
            type={inputType}
            value={inputValue}
            {...props}
          />
          <Button
            aria-hidden
            disabled={!clearable}
            className="px-0"
            data-testid="combobox-clear"
            onClick={ev => {
              clear();
              ev.preventDefault();
              ev.stopPropagation();
            }}
            style={{
              opacity: clearable ? 1 : 0,
              display: clearable ? undefined : 'none',
            }}
            tabIndex={-1}
            title="Clear value"
          >
            ×
          </Button>
          <Button
            aria-hidden
            className="pl-0 pr-2"
            data-testid="combobox-caret"
            disabled={disabled}
            onClick={ev => {
              ev.preventDefault();
              ev.stopPropagation();
            }}
            onMouseDown={ev => {
              ev.preventDefault();
              ev.stopPropagation();
              setOpen(!open);
              if (!open) {
                setTimeout(() => inputElement?.current?.focus(), 0);
              }
            }}
            tabIndex={-1}
            title="Toggle options menu"
          >
            <Icon name={open ? 'caret-up' : 'caret-down'} fixedWidth />
          </Button>
        </InputGroup>
      </DropdownToggle>
      <DropdownMenu
        data-testid="combobox-menu"
        style={{ maxHeight: menuMaxHeight }}
        {...dropdownProps}
        ref={dropdownMenu}
        role="listbox"
        aria-activedescendant={
          visibleOptions[focusedOptionIndex] &&
          `option-${makeKey(visibleOptions[focusedOptionIndex].value)}`
        }
      >
        <DropdownOptions
          options={optionsProp}
          visibleOptions={visibleOptions}
          focusedOptionIndex={focusedOptionIndex}
          focusedOption={focusedOption}
          setFocusedOptionIndex={setFocusedOptionIndex}
          selectOption={selectOption}
          value={value}
        />
        {noMatches && renderNoOptions()}
      </DropdownMenu>
    </Dropdown>
  );
}

Combobox.displayName = 'Combobox';
Combobox.defaultProps = defaultProps;

export default Combobox;
